import type { APISharedProfileResponse } from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'
import type { SeriesItem } from './chart'
import {
  lifestyleSignal,
  type LifestyleSignal,
  type LifestyleSignalId,
} from './lifestyle'

export interface LifestyleOverlay {
  signal: LifestyleSignal
  /**
   * One lifestyle signal's daily values ALIGNED to the chart's daily calendar
   * domain (same array the skin series uses), so the existing zoom-window +
   * Path machinery draws it with zero special-casing. `value` is null on any
   * domain day the provider had no reading, so the overlay line breaks at gaps.
   */
  series: SeriesItem[]
  /** The most recent NON-null value inside the domain, or null when empty. */
  latest: number | null
  /** True when at least one domain day has a value. */
  hasData: boolean
  /** Min/max over the present (non-null) values, for the right axis. 0/0 when empty. */
  min: number
  max: number
}

/**
 * Project the shared lifestyle days into ONE signal's series aligned to the skin
 * chart's daily `domain`, so lifestyle overlays the metric trend on a shared date
 * axis (a second, right-hand-scaled line). Measurement-only: reads the allowlisted
 * `lifestyle.data` and formats nothing (the chart formats via `overlay.signal`).
 * Days outside the domain are dropped; domain days with no reading stay null so
 * the line breaks at gaps rather than fabricating a value.
 */
export function buildLifestyleOverlay(
  profile: APISharedProfileResponse,
  signalId: LifestyleSignalId,
  domain: string[]
): LifestyleOverlay {
  const signal = lifestyleSignal(signalId)
  const byDay = new Map<string, number>()
  for (const day of profile.lifestyle.data) {
    const v = signal.valueOf(day)
    if (v !== null) byDay.set(sDayJs(day.date).format('YYYY-MM-DD'), v)
  }
  const series: SeriesItem[] = domain.map((day) => ({
    value: byDay.get(day) ?? null,
    isScan: false,
    scanId: null,
  }))

  const present = series
    .map((s) => s.value)
    .filter((v): v is number => v !== null)

  let latest: number | null = null
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i].value !== null) {
      latest = series[i].value
      break
    }
  }

  return {
    signal,
    series,
    latest,
    hasData: present.length > 0,
    min: present.length ? Math.min(...present) : 0,
    max: present.length ? Math.max(...present) : 0,
  }
}
